import { useTranslation } from "@hireroo/i18n";
import { ScreeningListForm } from "@hireroo/validator";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import { styled, useTheme } from "@mui/material/styles";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { useController } from "react-hook-form";

import InviteCandidateButtonWithDialog, {
  InviteCandidateButtonWithDialogProps,
} from "../../../../ms-components/Spot/InviteCandidateButtonWithDialog/InviteCandidateButtonWithDialog";
import ButtonWithTooltip, { ButtonWithTooltipProps } from "../../../../primitive/Button/ButtonWithTooltip/ButtonWithTooltip";
import Link, { LinkProps } from "../../../../primitive/Link/Link";
import ActionMenu, { ActionMenuProps } from "../ActionMenu/ActionMenu";
import ActivateSwitch, { ActivateSwitchProps } from "../ActivateSwitch/ActivateSwitch";
import Meta, { MetaProps } from "../Meta/Meta";

const SELECTED_CLASS_NAME = "__selected__";
const EllipsisText = styled(Typography)(() => ({
  textOverflow: "ellipsis",
  fontSize: "14px",
  maxWidth: "150px",
  display: "inline-block",
  overflow: "hidden",
  whiteSpace: "nowrap",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  borderTop: "1px solid",
  borderBottom: "1px solid",
  borderColor: theme.palette.Other.Divider,
  display: "table-row",
  [`&.${SELECTED_CLASS_NAME}`]: {
    backgroundColor: theme.palette["Secondary/Shades"].p8,
  },
}));

const StyledCheckbox = styled(Checkbox)(() => ({
  width: "38px",
  height: "38px",
  padding: "9px",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: "none",
  align: "center",
  whiteSpace: "nowrap",
  wordBreak: "keep-all",
  display: "table-cell",
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingRight: theme.spacing(2),
  paddingLeft: theme.spacing(2),
}));

const StyledTypography = styled(Typography)(() => ({
  fontSize: "14px",
}));

const CustomTableRow: React.FC<LinkProps> = props => {
  // FIXME I would like to ask the mui team how to define this type definition accurately.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return <ButtonBase as={Link} {...props} disableRipple />;
};

type Mode = "DEFAULT" | "SELECTABLE";
export type ScreeningTableRowProps = {
  id: string;
  name: string;
  disabled?: boolean;
  mode: Mode;
  href: string;
  onClick: () => void;
  actionMenu: ActionMenuProps;
  meta: MetaProps;
  numCandidates: number;
  numRemain?: number;
  creator: {
    label: string;
    src?: string;
  };
  candidateButton: Pick<ButtonWithTooltipProps, "onClick">;
  activateSwitch: ActivateSwitchProps;
  tags: string[];
  inviteCandidate: Omit<InviteCandidateButtonWithDialogProps, "variant">;
};

const ScreeningTableRow: React.FC<ScreeningTableRowProps> = props => {
  const { mode } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { field } = useController<Record<string, ScreeningListForm.ScreeningListItem>>({
    name: props.name,
  });

  const checkboxProps: CheckboxProps = {
    checked: field.value.selected,
    color: "secondary",
    size: "small",
    onClick: event => {
      event.stopPropagation();
    },
    onChange: (_event, checked) => {
      const newFieldValue: ScreeningListForm.ScreeningListItem = {
        ...field.value,
        selected: checked,
      };
      field.onChange(newFieldValue);
    },
  };

  const candidatesButtonProps: ButtonWithTooltipProps = {
    ...props.candidateButton,
    children: props.numCandidates,
    onClick: event => {
      event.stopPropagation();
      event.preventDefault();
      props.candidateButton.onClick?.(event);
    },
    endIcon: <KeyboardArrowRightOutlinedIcon fontSize="small" />,
    size: "small",
    color: "outline-only",
    variant: "text",
    title: props.numCandidates > 0 ? t("候補者一覧に移動") : "",
    disabled: props.numCandidates === 0,
  };

  return (
    <StyledTableRow
      as={!props.disabled ? CustomTableRow : undefined}
      // FIXME I would like to ask the mui team how to define this type definition accurately.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      component={"a"}
      href={mode === "SELECTABLE" ? "" : props.href}
      className={field.value.selected ? SELECTED_CLASS_NAME : undefined}
      onClick={event => {
        event.stopPropagation();
        event.preventDefault();
        if (mode === "SELECTABLE") {
          const newFieldValue: ScreeningListForm.ScreeningListItem = {
            ...field.value,
            selected: !field.value.selected,
          };
          field.onChange(newFieldValue);
        } else {
          props.onClick?.();
        }
      }}
    >
      <StyledTableCell component="div" align="center" sx={{ width: "38px" }}>
        <StyledCheckbox {...checkboxProps} />
      </StyledTableCell>
      <StyledTableCell component="div">
        <Meta {...props.meta} />
      </StyledTableCell>
      <StyledTableCell component="div" sx={{ maxWidth: "250px" }}>
        <Tooltip title={props.tags.join(", ")}>
          <EllipsisText>{props.tags.join(" ")}</EllipsisText>
        </Tooltip>
      </StyledTableCell>
      <StyledTableCell component="div" align="left">
        <ActivateSwitch {...props.activateSwitch} />
      </StyledTableCell>
      <StyledTableCell component="div" align="center">
        <Stack direction="row" alignItems="center" alignSelf="center" justifyContent="center">
          <ButtonWithTooltip {...candidatesButtonProps} />
        </Stack>
      </StyledTableCell>
      <StyledTableCell component="div" align="left">
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
          <StyledTypography>{props.numRemain ?? t("無制限")}</StyledTypography>
          {props.numRemain === 0 && props.activateSwitch.status === "ACTIVE" && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Tooltip title={t("追加でテストの発行が必要な場合は受験可能な定員数を増やす必要があります。")}>
                <WarningRoundedIcon sx={{ color: theme.palette.warning.light }} fontSize="small" />
              </Tooltip>
              <StyledTypography sx={{ color: theme.palette.warning.light }}>{t("残枠不足")}</StyledTypography>
            </Stack>
          )}
        </Stack>
      </StyledTableCell>
      <StyledTableCell component="div">
        <InviteCandidateButtonWithDialog {...props.inviteCandidate} variant="outlined" />
      </StyledTableCell>
      <StyledTableCell component="div" align="right">
        <ActionMenu {...props.actionMenu} disabled={mode === "SELECTABLE"} tooltipText={t("複数選択中はこの操作はできません。")} />
      </StyledTableCell>
    </StyledTableRow>
  );
};

ScreeningTableRow.displayName = "ScreeningTableRow";

export default ScreeningTableRow;
